import { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { codeApi, CodeListItem } from '../../api/codeApi';
import toast from 'react-hot-toast';

const CODE_TYPES = [
    { id: 'ICD10', label: 'ICD-10-CM' },
    { id: 'ICD10PCS', label: 'ICD-10-PCS' },
    { id: 'HCPCS', label: 'HCPCS' },
    { id: 'NDC', label: 'NDC' },
    { id: 'LOINC', label: 'LOINC' },
    { id: 'RXNORM', label: 'RxNorm' },
];

interface DropdownState {
    items: CodeListItem[];
    page: number;
    hasMore: boolean;
    total: number;
    loading: boolean;
    initialLoading: boolean;
}

const SkeletonRow = ({ width }: { width: string }) => (
    <div className="px-3 py-2 flex items-center gap-2 animate-pulse">
        <div className="w-12 h-3.5 bg-gray-100 rounded flex-shrink-0"/>
        <div className="h-3 bg-gray-100 rounded" style={{ width: '6px', flexShrink: 0 }}/>
        <div className="h-3 bg-gray-100 rounded flex-1" style={{ maxWidth: width }}/>
    </div>
);

const WIDTHS = ['55%', '70%', '48%', '65%', '72%', '52%', '68%', '58%'];

const CodeDropdown = ({ codeType, isOpen, onClose }: {
    codeType: typeof CODE_TYPES[0];
    isOpen: boolean;
    onClose: () => void;
}) => {
    const navigate = useNavigate();
    const scrollRef = useRef<HTMLDivElement>(null);
    const [state, setState] = useState<DropdownState>({
        items: [], page: 1, hasMore: true, total: 0,
        loading: false, initialLoading: false,
    });

    const fetchCodes = useCallback(async (page: number, reset = false) => {
        setState(prev => ({ ...prev, loading: true, initialLoading: page === 1 }));
        try {
            const data = await codeApi.listCodes(codeType.id, page, 50);
            setState(prev => ({
                items: reset ? data.codes : [...prev.items, ...data.codes],
                page, hasMore: data.hasMore, total: data.total,
                loading: false, initialLoading: false,
            }));
        } catch {
            setState(prev => ({ ...prev, loading: false, initialLoading: false }));
        }
    }, [codeType.id]);

    useEffect(() => {
        if (!isOpen) return;
        setState({ items: [], page: 1, hasMore: true, total: 0, loading: false, initialLoading: true });
        fetchCodes(1, true);
    }, [isOpen, codeType.id]);

    const handleScroll = useCallback(() => {
        const el = scrollRef.current;
        if (!el || state.loading || !state.hasMore) return;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40)
            fetchCodes(state.page + 1);
    }, [state.loading, state.hasMore, state.page, fetchCodes]);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        el.addEventListener('scroll', handleScroll);
        return () => el.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    if (!isOpen) return null;

    return (
        <div className="absolute top-full left-0 mt-1 w-80 bg-white border border-gray-100 rounded-xl shadow-xl z-50 overflow-hidden">
            <div className="px-3 py-2.5 border-b border-gray-50 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <span className="text-[11px] font-bold text-gray-600 uppercase tracking-wider">{codeType.label}</span>
                    {state.total > 0 && (
                        <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full font-mono">{state.total.toLocaleString()}</span>
                    )}
                </div>
                <button onClick={() => { navigate(`/codes/${codeType.id}`); onClose(); }}
                    className="text-[10px] text-teal-600 hover:underline font-bold">
                    View all →
                </button>
            </div>
            <div ref={scrollRef} className="max-h-72 overflow-y-auto py-1">
                {state.initialLoading && WIDTHS.map((w, i) => <SkeletonRow key={i} width={w}/>)}
                {!state.initialLoading && state.items.map((item, i) => (
                    <button key={`${item.code}-${i}`}
                        onClick={() => { navigate(`/code/${item.codeType}/${item.code}`); onClose(); }}
                        className="w-full text-left px-3 py-1.5 hover:bg-teal-50 transition-colors flex items-center gap-2 group">
                        <span className="text-[11px] font-bold text-teal-700 font-mono flex-shrink-0 min-w-[60px]">{item.code}</span>
                        <span className="text-[10px] text-gray-300 flex-shrink-0">—</span>
                        <span className="text-[11px] text-gray-600 group-hover:text-gray-800 truncate">{item.description}</span>
                    </button>
                ))}
                {!state.initialLoading && state.loading && (
                    <div className="py-1">{['58%', '72%', '48%'].map((w, i) => <SkeletonRow key={i} width={w}/>)}</div>
                )}
                {!state.initialLoading && !state.hasMore && state.items.length > 0 && (
                    <div className="text-center py-2">
                        <button onClick={() => { navigate(`/codes/${codeType.id}`); onClose(); }}
                            className="text-[10px] text-teal-600 hover:underline font-semibold">
                            View all {state.total.toLocaleString()} codes →
                        </button>
                    </div>
                )}
                {!state.initialLoading && !state.loading && state.items.length === 0 && (
                    <p className="text-center text-[11px] text-gray-400 py-4">No codes found</p>
                )}
            </div>
        </div>
    );
};

interface HeaderProps {
    onMenuToggle?: () => void;
    sidebarCollapsed?: boolean;
}

const Header = ({ onMenuToggle }: HeaderProps) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, isLoggedIn, logout } = useAuth();
    const [openType, setOpenType] = useState<string | null>(null);
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const headerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleOutside = (e: MouseEvent) => {
            if (headerRef.current && !headerRef.current.contains(e.target as Node)) {
                setOpenType(null);
                setUserMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleOutside);
        return () => document.removeEventListener('mousedown', handleOutside);
    }, []);

    useEffect(() => {
        setOpenType(null);
        setMobileNavOpen(false);
        setUserMenuOpen(false);
    }, [location.pathname]);

    const handleLogout = () => {
        logout();
        toast.success('Logged out successfully');
        navigate('/');
    };

    return (
        <header ref={headerRef} className="bg-white border-b border-gray-100 sticky top-0 z-40 shadow-sm">
            <div className="px-4">
                <div className="flex items-center h-14 gap-3">

                    {/* ✅ Sidebar toggle — only when logged in (mobile) */}
                    {isLoggedIn && onMenuToggle && (
                        <button onClick={onMenuToggle}
                            className="md:hidden p-2 text-gray-500 hover:bg-gray-50 rounded-lg flex-shrink-0">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                <path d="M4 6h16M4 12h16M4 18h16"/>
                            </svg>
                        </button>
                    )}

                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-2 flex-shrink-0">
                        <div className="w-7 h-7 bg-teal-600 rounded-lg flex items-center justify-center shadow-sm">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
                                <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
                            </svg>
                        </div>
                        <span className="text-[14px] font-bold text-gray-800 hidden sm:block">
                            Med<span className="text-teal-600">Code</span>
                            <span className="text-gray-400 font-medium">Lookup</span>
                        </span>
                    </Link>

                    {/* Desktop Nav */}
                    <nav className="hidden md:flex items-center gap-0.5 flex-1">
                        {CODE_TYPES.map(ct => (
                            <div key={ct.id} className="relative">
                                <button
                                    onClick={() => setOpenType(prev => prev === ct.id ? null : ct.id)}
                                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-semibold transition-all ${
                                        openType === ct.id ? 'bg-teal-50 text-teal-700' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                                    }`}
                                >
                                    {ct.label}
                                    <svg className={`transition-transform duration-200 ${openType === ct.id ? 'rotate-180' : ''}`}
                                        width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                        <path d="m6 9 6 6 6-6"/>
                                    </svg>
                                </button>
                                <CodeDropdown codeType={ct} isOpen={openType === ct.id} onClose={() => setOpenType(null)}/>
                            </div>
                        ))}
                        <div className="w-px h-5 bg-gray-200 mx-1"/>
                        <Link to="/ai-tools"
                            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-semibold transition-all ${
                                location.pathname === '/ai-tools' ? 'bg-teal-50 text-teal-700' : 'text-gray-600 hover:bg-gray-50'
                            }`}>
                            AI Tools
                            <span className="text-[9px] bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded-full font-bold">AI</span>
                        </Link>
                        <Link to="/about"
                            className={`px-3 py-2 rounded-lg text-[12px] font-semibold transition-all ${
                                location.pathname === '/about' ? 'bg-teal-50 text-teal-700' : 'text-gray-600 hover:bg-gray-50'
                            }`}>
                            About
                        </Link>
                    </nav>

                    {/* Right */}
                    <div className="ml-auto flex items-center gap-2">
                        <button onClick={() => navigate('/search')}
                            className="p-2 text-gray-500 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-all">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                            </svg>
                        </button>

                        {isLoggedIn && user ? (
                            <div className="relative">
                                <button onClick={() => setUserMenuOpen(o => !o)}
                                    className="flex items-center gap-2 px-3 py-1.5 border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all">
                                    <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                                        <span className="text-[10px] font-bold text-teal-700">{user.fullName?.charAt(0).toUpperCase()}</span>
                                    </div>
                                    <span className="text-[12px] font-semibold text-gray-700 hidden sm:block max-w-[100px] truncate">{user.fullName}</span>
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                        <path d="m6 9 6 6 6-6"/>
                                    </svg>
                                </button>

                                {userMenuOpen && (
                                    <div className="absolute top-full right-0 mt-1.5 w-52 bg-white border border-gray-100 rounded-xl shadow-lg z-50 overflow-hidden">
                                        <div className="px-4 py-3 border-b border-gray-50">
                                            <p className="text-[12px] font-bold text-gray-800 truncate">{user.fullName}</p>
                                            <p className="text-[11px] text-gray-400 truncate">{user.email}</p>
                                            {user.currentPlan && (
                                                <span className="inline-block mt-1.5 text-[10px] font-bold bg-teal-50 text-teal-700 border border-teal-100 px-2 py-0.5 rounded-full">
                                                    {user.currentPlan} Plan
                                                </span>
                                            )}
                                        </div>
                                        <button onClick={handleLogout}
                                            className="w-full text-left px-4 py-2.5 text-[12px] text-red-500 hover:bg-red-50 transition-colors flex items-center gap-2.5 font-medium">
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                                                <polyline points="16 17 21 12 16 7"/>
                                                <line x1="21" y1="12" x2="9" y2="12"/>
                                            </svg>
                                            Sign Out
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="flex items-center gap-1.5">
                                <Link to="/login" className="px-3 py-1.5 text-[12px] font-semibold text-gray-600 hover:text-teal-600 transition-colors hidden sm:block">
                                    Sign In
                                </Link>
                                <Link to="/register" className="px-3.5 py-1.5 text-[12px] font-bold bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-all shadow-sm">
                                    Get Started
                                </Link>
                            </div>
                        )}

                        {/* Mobile nav toggle — only when NOT logged in */}
                        {!isLoggedIn && (
                            <button className="md:hidden p-2 text-gray-500 hover:bg-gray-50 rounded-lg"
                                onClick={() => setMobileNavOpen(o => !o)}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                    {mobileNavOpen ? <path d="M18 6 6 18M6 6l12 12"/> : <path d="M4 6h16M4 12h16M4 18h16"/>}
                                </svg>
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Mobile Nav — only for non-logged-in */}
            {mobileNavOpen && !isLoggedIn && (
                <div className="md:hidden border-t border-gray-100 bg-white px-4 py-3 space-y-1">
                    {CODE_TYPES.map(ct => (
                        <button key={ct.id} onClick={() => { navigate(`/codes/${ct.id}`); setMobileNavOpen(false); }}
                            className="w-full text-left px-3 py-2.5 text-[13px] font-semibold text-gray-700 hover:bg-teal-50 hover:text-teal-700 rounded-lg transition-all">
                            {ct.label}
                        </button>
                    ))}
                    <div className="border-t border-gray-100 pt-2 space-y-1">
                        <Link to="/ai-tools" className="block px-3 py-2.5 text-[13px] font-semibold text-gray-700 hover:bg-teal-50 rounded-lg" onClick={() => setMobileNavOpen(false)}>AI Tools</Link>
                        <Link to="/about" className="block px-3 py-2.5 text-[13px] font-semibold text-gray-700 hover:bg-teal-50 rounded-lg" onClick={() => setMobileNavOpen(false)}>About</Link>
                        <Link to="/register" className="block px-3 py-2.5 text-[13px] font-bold text-teal-600 hover:bg-teal-50 rounded-lg" onClick={() => setMobileNavOpen(false)}>
                            Get Started — Free
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Header;